/**
 * 환경설정 &gt; 자동분류 (migration 161, 2026-09-05 사용자 지시).
 *
 * 스위치 하나(기본 끔) + 규칙 목록(추가 · 전체삭제 · 순서변경) + 규칙 팝업.
 *
 * ★★★ **저장바가 없다** — 스팸 탭과 같다(정본 P2-1 「반영 시점의 단위는 탭」).
 *    목록을 고치는 탭이라 추가·삭제는 제출이 아니고, 스위치도 고른 순간 반영된다.
 *    저장바가 없으므로 켬/끔은 `Switch` 다(정본 B7).
 * ★★★ **순서가 곧 규칙이다** — 위에서부터 처음 맞는 하나만 적용된다. 그래서 번호를 적고,
 *    「순서변경」 모드에서 위·아래로 옮긴다(정본 E1-3). 옮기면 그 자리에서 저장한다.
 * ★★ **꺼져 있어도 목록은 보인다.** 규칙은 만들어 두고 켜는 것이라 값이 살아 있다 —
 *    다만 「지금은 돌지 않는다」를 화면이 말한다(안 적으면 도는 줄 안다).
 */

import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  clearMailFilterRules, createMailFilterRule, deleteMailFilterRule, listMailFilterRules,
  listMailFolders, listMailTags, reorderMailFilterRules, updateMailFilterRule,
  type MailFilterRule, type MailFilterRuleInput, type MailFolderNode, type MailTag,
} from '../../api/mail';
import { getMySettings, patchMySettings } from '../../api/users';
import { ChevronDown, ChevronUp, Edit3, Plus } from '../../icons';
import { Button } from '../../components/shared/Button';
import { Switch } from '../../components/shared/Switch';
import { Table, type TableColumn } from '../../components/shared/Table';
import { tMail, type MailI18nKey } from '../../lib/mail-i18n';
import { MailConfirmDialog } from './MailConfirmDialog';
import { MailFilterRuleModal } from './MailFilterRuleModal';

/** 조건 칸에 적는 말 — 규칙을 열어 보지 않고도 무엇에 걸리는지 알 수 있어야 한다. */
function conditionText(rule: MailFilterRule): string[] {
  const out: string[] = [];
  if (rule.addresses.length > 0) {
    const head = rule.addresses[0]!;
    const rest = rule.addresses.length - 1;
    const label = tMail(rule.addressKind === 'TO' ? 'mail.filterset.cond.to' : 'mail.filterset.cond.from');
    out.push(`${label} ${head}${rest > 0 ? ` ${tMail('mail.filterset.cond.more').replace('{n}', String(rest))}` : ''}`);
  }
  if ((rule.subjectContains ?? '').trim() !== '') {
    out.push(tMail('mail.filterset.cond.subject').replace('{v}', rule.subjectContains!));
  }
  return out;
}

type Ask = { kind: 'delete'; rule: MailFilterRule } | { kind: 'clear' };

export function MailSettingsFilter() {
  const [enabled, setEnabled] = useState(false);
  const [rules, setRules] = useState<MailFilterRule[]>([]);
  const [folders, setFolders] = useState<MailFolderNode[]>([]);
  const [tags, setTags] = useState<MailTag[]>([]);
  const [loading, setLoading] = useState(true);
  const [note, setNote] = useState<{ ok: boolean; text: string } | null>(null);
  const [ordering, setOrdering] = useState(false);
  const [editFor, setEditFor] = useState<{ rule: MailFilterRule | null } | null>(null);
  const [ask, setAsk] = useState<Ask | null>(null);
  const [busy, setBusy] = useState(false);
  const [modalErr, setModalErr] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    Promise.all([getMySettings(), listMailFilterRules(), listMailFolders(), listMailTags()])
      .then(([s, r, f, t]) => {
        if (cancelled) return;
        setEnabled(s.mailFilterEnabled === true);
        setRules(r);
        setFolders(f);
        setTags(t);
      })
      .catch(() => { if (!cancelled) setNote({ ok: false, text: tMail('mail.filterset.load_failed') }); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, []);

  /** 스위치 — 고른 순간 저장하고, 실패하면 **되돌린다**. */
  const toggle = useCallback(async (next: boolean) => {
    setEnabled(next);
    setNote(null);
    try {
      await patchMySettings({ mailFilterEnabled: next });
      setNote({ ok: true, text: tMail('mail.filterset.saved') });
    } catch (err) {
      setEnabled(!next);
      const status = (err as { response?: { status?: number } }).response?.status;
      setNote({
        ok: false,
        text: tMail(status === 400 ? 'mail.filterset.legacy' : 'mail.filterset.save_failed'),
      });
    }
  }, []);

  /** 하는 일 한 벌 — 실패는 팝업이 열려 있으면 팝업 안에, 아니면 화면 아래에 **한 자리에만**. */
  const run = useCallback(async (
    fn: () => Promise<void>, failKey: MailI18nKey, inModal = false,
  ): Promise<boolean> => {
    setBusy(true);
    setModalErr(null);
    setNote(null);
    try {
      await fn();
      setNote({ ok: true, text: tMail('mail.filterset.saved') });
      return true;
    } catch (err) {
      const code = (err as { response?: { data?: { code?: string } } }).response?.data?.code;
      const msg = code === 'NO_CONDITION' ? tMail('mail.filterset.err_cond')
        : code === 'NO_ACTION' ? tMail('mail.filterset.err_action')
          : code === 'INVALID_ADDRESS' ? tMail('mail.filterset.err_addr')
            : tMail(failKey);
      if (inModal) setModalErr(msg); else setNote({ ok: false, text: msg });
      return false;
    } finally {
      setBusy(false);
    }
  }, []);

  const move = useCallback(async (index: number, delta: number) => {
    const next = [...rules];
    const to = index + delta;
    if (to < 0 || to >= next.length) return;
    [next[index], next[to]] = [next[to]!, next[index]!];
    // 화면을 먼저 옮긴다 — 서버를 기다리면 두 번 누를 때 순서가 튄다.
    setRules(next);
    const ok = await run(async () => {
      const saved = await reorderMailFilterRules(next.map((r) => r.id));
      if (saved.length > 0) setRules(saved);
    }, 'mail.filterset.save_failed');
    if (!ok) setRules(rules);
  }, [rules, run]);

  const submitRule = useCallback(async (input: MailFilterRuleInput) => {
    const editing = editFor?.rule ?? null;
    const ok = await run(async () => {
      if (editing) await updateMailFilterRule(editing.id, input);
      else await createMailFilterRule(input);
      setRules(await listMailFilterRules());
    }, 'mail.filterset.err_failed', true);
    if (ok) setEditFor(null);
  }, [editFor, run]);

  const columns: ReadonlyArray<TableColumn<MailFilterRule>> = useMemo(() => [
    {
      key: 'name',
      header: tMail('mail.filterset.col.name'),
      rowHeader: true,
      render: (r, i) => (
        <span className="filtrule__name">
          {/* 순서가 뜻이라 **번호를 적는다** — 없으면 무엇이 먼저인지 눈으로 못 센다(정본 E1-3). */}
          <span className="filtrule__no" aria-hidden>{i + 1}</span>
          <span className="filtrule__nm">{r.name}</span>
        </span>
      ),
    },
    {
      key: 'cond',
      header: tMail('mail.filterset.col.cond'),
      render: (r) => (
        <span className="filtrule__cond">
          {conditionText(r).map((line) => <span key={line}>{line}</span>)}
          {conditionText(r).length > 1 && (
            <span className="filtrule__mode">
              {tMail(r.matchMode === 'ANY' ? 'mail.filterset.cond.any' : 'mail.filterset.cond.all')}
            </span>
          )}
        </span>
      ),
    },
    {
      key: 'action',
      header: tMail('mail.filterset.col.action'),
      width: '220px',
      render: (r) => (
        <span className="filtrule__cond">
          {/* ★ 「{이름} 으로 이동」처럼 조사를 붙이지 않는다 — 받침에 따라 「으로/로」가 갈려
              한 틀로 못 만든다. 칸 이름이 이미 「보관메일함/태그」라 이름만으로 읽힌다. */}
          {r.folder && <span>{r.folder.name}</span>}
          {r.tag && <span className="mailtag" data-tagcolor={r.tag.color}>{r.tag.name}</span>}
          {/* 메일함을 지우면 규칙은 남고 옮기기만 멈춘다 — 그 사실을 적는다. */}
          {!r.folder && !r.tag && <span className="mbox__mut">{tMail('mail.filterset.act.gone')}</span>}
        </span>
      ),
    },
    ordering
      ? {
        key: 'order',
        header: tMail('mail.filterset.col.order'),
        width: '110px',
        render: (r, i) => (
          <span className="mbox__acts">
            <Button
              variant="secondary" size="sm" iconOnly
              disabled={i === 0 || busy}
              aria-label={tMail('mail.filterset.up').replace('{n}', r.name)}
              onClick={() => { void move(i, -1); }}
            >
              <ChevronUp size={14} aria-hidden />
            </Button>
            <Button
              variant="secondary" size="sm" iconOnly
              disabled={i === rules.length - 1 || busy}
              aria-label={tMail('mail.filterset.down').replace('{n}', r.name)}
              onClick={() => { void move(i, 1); }}
            >
              <ChevronDown size={14} aria-hidden />
            </Button>
          </span>
        ),
      }
      : {
        key: 'manage',
        header: tMail('mail.filterset.col.manage'),
        width: '170px',
        render: (r) => (
          <span className="mbox__acts">
            <Button
              variant="secondary" size="sm" disabled={busy}
              iconLeft={<Edit3 size={13} aria-hidden />}
              onClick={() => { setModalErr(null); setEditFor({ rule: r }); }}
            >
              {tMail('mail.filterset.edit')}
            </Button>
            <Button
              variant="quiet-danger" size="sm" disabled={busy}
              onClick={() => setAsk({ kind: 'delete', rule: r })}
            >
              {tMail('mail.filterset.delete')}
            </Button>
          </span>
        ),
      },
  ], [ordering, busy, rules.length, move]);

  if (loading) return <div className="wc-set__body"><p className="wc-set__hint">…</p></div>;

  return (
    <div className="wc-set__body">
      <section className="wc-set__sec" aria-labelledby="mfilt-h">
        <h2 className="wc-set__sech" id="mfilt-h">{tMail('mail.filterset.sec.use')}</h2>
        <div className="wc-set__rows">
          <div className="wc-set__row">
            <span className="wc-set__label">{tMail('mail.filterset.enabled')}</span>
            <div className="wc-set__ctl">
              {/* 저장바가 없는 화면이라 Switch 다(정본 B7) — 고른 순간 반영된다. */}
              <Switch
                checked={enabled}
                onCheckedChange={(next) => { void toggle(next); }}
                label={tMail('mail.filterset.enabled_label')}
              />
              {!enabled && <p className="wc-set__hint">{tMail('mail.filterset.off_hint')}</p>}
              <p className={note?.ok === false ? 'wc-set__bad' : 'wc-set__ok'} role="status">
                {note?.text ?? ''}
              </p>
            </div>
          </div>
        </div>
      </section>

      <section className="wc-set__sec" aria-labelledby="mfiltl-h">
        <div className="wc-set__sechead">
          <h2 className="wc-set__sech" id="mfiltl-h">{tMail('mail.filterset.sec.rule')}</h2>
          <Button
            variant="primary" size="sm"
            iconLeft={<Plus size={14} aria-hidden />}
            onClick={() => { setModalErr(null); setEditFor({ rule: null }); }}
          >
            {tMail('mail.filterset.add')}
          </Button>
          <Button
            variant="secondary" size="sm"
            disabled={rules.length < 2}
            onClick={() => setOrdering((v) => !v)}
          >
            {tMail(ordering ? 'mail.filterset.reorder_done' : 'mail.filterset.reorder')}
          </Button>
          <Button
            variant="quiet-danger" size="sm"
            disabled={rules.length === 0 || busy}
            onClick={() => setAsk({ kind: 'clear' })}
          >
            {tMail('mail.filterset.clear')}
          </Button>
        </div>

        <Table
          caption={tMail('mail.filterset.sec.rule')}
          columns={columns}
          rows={rules}
          rowKey={(r) => r.id}
          empty={<span>{tMail('mail.filterset.none')}</span>}
          wrapClassName="mbox__tablewrap"
        />
        <p className="wc-set__hint">{tMail('mail.filterset.order_hint')}</p>
      </section>

      {editFor && (
        <MailFilterRuleModal
          target={editFor.rule}
          folders={folders}
          tags={tags}
          busy={busy}
          error={modalErr}
          onClose={() => { setEditFor(null); setModalErr(null); }}
          onSubmit={(input) => { void submitRule(input); }}
        />
      )}

      <MailConfirmDialog
        open={ask !== null}
        danger
        busy={busy}
        title={ask?.kind === 'delete'
          ? tMail('mail.filterset.del_title').replace('{n}', ask.rule.name)
          : tMail('mail.filterset.clear_title')}
        description={ask?.kind === 'delete'
          ? tMail('mail.filterset.del_desc')
          : tMail('mail.filterset.clear_desc')}
        confirmLabel={tMail('mail.filterset.delete')}
        onCancel={() => setAsk(null)}
        onConfirm={() => {
          const cur = ask;
          if (!cur) return;
          void (async () => {
            const ok = await run(async () => {
              if (cur.kind === 'delete') await deleteMailFilterRule(cur.rule.id);
              else await clearMailFilterRules();
              setRules(await listMailFilterRules());
            }, 'mail.filterset.save_failed');
            if (ok) { setAsk(null); setOrdering(false); }
          })();
        }}
      />
    </div>
  );
}
